import { useEffect, useMemo, useState } from 'react'
import { FaEdit } from 'react-icons/fa'
import MatchInfo from './MatchInfo'
import ComboBox from '../../../components/ComboBox'
import moment from 'moment/moment'
import { isReversedTeamPair } from '../eventMatching'

const toMinutes = (value) => {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return date.getHours() * 60 + date.getMinutes()
}

const timeDistance = (left, right) => {
  const a = toMinutes(left)
  const b = toMinutes(right)
  if (a == null || b == null) return Number.POSITIVE_INFINITY
  const difference = Math.abs(a - b)
  return Math.min(difference, 1440 - difference)
}

function EventSelect({
  event909,
  leagueName,
  event555,
  eventId555,
  handleChange,
  matchId,
  getEvent,
  matched,
  rowIndex = 0,
  mobile = false,
}) {
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const selected = event555.filter(
      (v) => v.id.toString() === eventId555?.toString()
    )[0]
    setSelectedEvent(selected ?? null)
  }, [event555, eventId555])

  const comboBoxOptions = useMemo(
    () =>
      event555
        .filter(
          (option) =>
            !isReversedTeamPair(
              event909.homeTeam,
              event909.awayTeam,
              option.homeName,
              option.awayName,
            ),
        )
        .map((option) => ({
          ...option,
          id: option.id?.toString() ?? option.id,
          description: `${option.description || '555Mix'}${option.kickoff ? ` · ${moment(option.kickoff).format('HH:mm')}` : ''}`,
          __timeDistance: timeDistance(event909.eventTime, option.kickoff),
        }))
        .sort((left, right) => left.__timeDistance - right.__timeDistance),
    [event555, event909],
  )

  const filterMatchOptions = (options, query) => {
    const normalizedQuery = query.trim().toLowerCase()
    return options.filter((option) => {
      if (!normalizedQuery) return true
      return [
        option.__label,
        option.__description,
        option.homeName,
        option.awayName,
      ].some((value) => value?.toLowerCase().includes(normalizedQuery))
    })
  }

  const handleOnChange = async (event, newValue, rapidId) => {
    setSelectedEvent(newValue ?? null)
    handleChange(event, newValue, rapidId)
  }

  const handleClickOpen = async () => {
    setOpen(true)
  }

  const handleClose = async () => {
    setOpen(false)
  }

  const isMatched = Boolean(matched)

  if (mobile) {
    return (
      <article className="rounded-lg border border-customBorder/20 bg-[#e7eefc] p-2 dark:border-custom_dark_secondary dark:bg-custom_dark_primary2">
        <MatchInfo open={open} handleClose={handleClose} event555={event555} event909={event909} matchId={matchId} getEvent={getEvent}/>
        <div className="mb-1.5 flex items-center justify-between text-[10px] font-semibold">
          <span className="text-customPrimaryText dark:text-customFontMilkWhite">
            {event909.eventTime ? moment(event909.eventTime).format('hh:mm A') : 'TBD'}
          </span>
          <span className={isMatched ? 'text-customVibrantGreen' : 'text-customFontRed'}>
            {isMatched ? 'MATCHED' : 'PENDING'}
          </span>
        </div>
        <div className="mb-2 flex flex-wrap items-center gap-1 text-xs font-semibold text-customInk dark:text-customFontMilkWhite">
          <span>{event909.homeTeam}</span>
          <span className="text-[10px] font-normal text-customSecondaryText dark:text-customMuted">vs</span>
          <span>{event909.awayTeam}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <ComboBox
              options={comboBoxOptions}
              value={selectedEvent}
              onChange={(newValue) => handleOnChange(null, newValue, event909.rapidEventId)}
              placeholder="Search 555 event"
              disabled={isMatched}
              emptyState="No 555 events found"
              className="w-full"
              compact
              disableShadow
              disableFocusRing
              filterOptions={filterMatchOptions}
              optionLimit={3}
            />
          </div>
          {isMatched ? (
            <button
              type="button"
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-sm text-customAccent transition hover:bg-customAccent hover:text-white focus:outline-none"
              onClick={handleClickOpen}
              aria-label="Edit match mapping"
            >
              <FaEdit />
            </button>
          ) : null}
        </div>
      </article>
    )
  }

  return (
    <tr className={`border-b border-customLightMilkWhite transition-colors hover:bg-customSky/80 dark:border-custom_dark_secondary/80 dark:hover:bg-custom_dark_primary ${rowIndex % 2 === 0 ? 'bg-[#e7eefc] dark:bg-custom_dark_primary2' : 'bg-[#d7e2f7] dark:bg-custom_dark_background'}`}>
      <td className="w-[112px] px-3 py-2 align-middle">
        <div className="flex flex-col text-[11px] font-semibold leading-4">
          <span className={`uppercase tracking-[0.12em] ${isMatched ? 'text-customVibrantGreen' : 'text-customFontRed'}`}>
            {isMatched ? 'MATCHED' : 'PENDING'}
          </span>
          <span className="text-customPrimaryText dark:text-customFontMilkWhite">
            {event909.eventTime ? moment(event909.eventTime).format('hh:mm A') : 'TBD'}
          </span>
        </div>
      </td>
      <td className="min-w-[180px] px-3 py-2 align-middle">
        <span className="text-xs italic font-semibold leading-5 text-customInk dark:text-customFontMilkWhite">
          {leagueName ?? 'N/A'}
        </span>
      </td>
      <td className="min-w-[240px] px-3 py-2 align-middle">
        <div className="flex items-center gap-2 whitespace-nowrap text-sm font-semibold leading-5 text-customInk dark:text-customFontMilkWhite">
          <span className="max-w-[180px] truncate">{event909.homeTeam}</span>
          <span className="shrink-0 text-xs font-normal text-customSecondaryText dark:text-customMuted">vs</span>
          <span className="max-w-[180px] truncate">{event909.awayTeam}</span>
        </div>
      </td>
      <td className="min-w-[280px] px-3 py-2 align-middle">
        <ComboBox
          options={comboBoxOptions}
          value={selectedEvent}
          onChange={(newValue) => handleOnChange(null, newValue, event909.rapidEventId)}
          placeholder="Search 555 event"
          disabled={isMatched}
          emptyState="No 555 events found"
          className="w-full"
          compact
          disableShadow
          disableFocusRing
          filterOptions={filterMatchOptions}
          optionLimit={3}
        />
      </td>
      <td className="w-[92px] px-3 py-2 text-center align-middle">
        {isMatched ? (
          <span className="text-xs font-semibold uppercase tracking-wide text-customVibrantGreen dark:text-customLightYellow">
            Matched
          </span>
        ) : (
          <span className="text-xs font-semibold uppercase tracking-wide text-customFontRed">
            Pending
          </span>
        )}
      </td>
      <td className="w-[64px] px-3 py-2 text-center align-middle">
        <MatchInfo open={open} handleClose={handleClose} event555={event555} event909={event909} matchId={matchId} getEvent={getEvent}/>
        {isMatched ? (
          <button
            type="button"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-sm text-customAccent transition hover:bg-customAccent hover:text-white focus:outline-none"
            onClick={handleClickOpen}
            aria-label="Edit match mapping"
          >
            <FaEdit />
          </button>
        ) : null}
      </td>
    </tr>
  )
}

export default EventSelect
